/**
 * Decorative radial glow — a soft, coloured light that sits BEHIND the
 * content of its section and OVER the section background. Purely
 * cosmetic: no layout footprint, no pointer events.
 *
 * <h3>Positioning model</h3>
 * The glow is {@code position: absolute; inset: 0}, so it fills the
 * nearest positioned ancestor. Every section wrapper it can land in —
 * the portal {@code <main>}, {@code Band}, {@code Container} — is a
 * {@code position: relative; isolation: isolate} stacking context, so
 * the glow's {@code z-index: -1} places it under that region's content
 * but still above its background. {@code pointerEvents: 'none'} means it
 * never eats clicks.
 *
 * <h3>Light / dark colours</h3>
 * Authors pick a separate start/end colour for light and dark mode. We
 * can't branch on the theme from an inline style, so the block injects a
 * tiny scoped {@code <style>}: the light gradient is the base rule and
 * the dark gradient overrides it under {@code :root[data-theme="dark"]}
 * — the portal's theme contract (see {@code themeMode.ts}). This is
 * fully server-rendered and flips instantly with the theme toggle, no
 * JavaScript on the block itself.
 *
 * <p>Every knob is authorable from the admin panel via
 * {@code seed-data/blocks/BackgroundGlow.json}.
 */
import type { CSSProperties } from 'react';
import { BlockRoot } from '../_shared/BlockRoot';

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/** Deterministic, SSR-stable class suffix from the gradient strings.
 *  No Date/random — two glows with identical colours share one rule. */
function hashKey(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

export interface BackgroundGlowProps {
  /** Horizontal centre of the glow, 0 (left) – 100 (right). */
  posX?: number;
  /** Vertical centre of the glow, 0 (top) – 100 (bottom). */
  posY?: number;
  /** Inner colour in light mode. Any CSS colour (hex/rgba/var()). */
  startColorLight?: string;
  /** Outer colour the glow fades to in light mode. Usually transparent. */
  endColorLight?: string;
  /** Inner colour in dark mode. */
  startColorDark?: string;
  /** Outer colour the glow fades to in dark mode. */
  endColorDark?: string;
  /** @deprecated Legacy single-colour prop — used as the light-mode
   *  start when {@link startColorLight} is unset. */
  startColor?: string;
  /** @deprecated Legacy single-colour prop — used as the light-mode
   *  end when {@link endColorLight} is unset. */
  endColor?: string;
  /** How far the glow reaches before hitting the end colour, as a %
   *  of the section. Bigger = larger, softer light. */
  radius?: number;
  /** Overall glow opacity, 0–1. */
  opacity?: number;
  /** Optional Gaussian blur (px) for an extra-diffuse edge. */
  blur?: number;
}

export function BackgroundGlow({
  posX = 20,
  posY = 30,
  startColorLight,
  endColorLight,
  startColorDark,
  endColorDark,
  startColor,
  endColor,
  radius = 60,
  opacity = 1,
  blur = 0,
}: BackgroundGlowProps) {
  const x = clamp(posX, 0, 100);
  const y = clamp(posY, 0, 100);
  const reach = clamp(radius, 0, 200);
  const o = clamp(opacity, 0, 1);
  const b = Math.max(0, blur);

  // Resolve each slot with graceful fallbacks: explicit light/dark →
  // legacy single colour → sensible default. Dark falls back to the
  // light choice so a glow authored before this split still themes.
  const sLight = startColorLight ?? startColor ?? 'rgba(59, 91, 219, 0.45)';
  const eLight = endColorLight ?? endColor ?? 'transparent';
  const sDark = startColorDark ?? sLight;
  const eDark = endColorDark ?? eLight;

  const grad = (start: string, end: string) =>
    `radial-gradient(circle at ${x}% ${y}%, ${start}, ${end} ${reach}%)`;
  const lightBg = grad(sLight, eLight);
  const darkBg = grad(sDark, eDark);

  const cls = `sb-bg-glow-${hashKey(`${lightBg}|${darkBg}`)}`;
  // Base rule = light. Only emit the dark override when it differs.
  const css =
    `.${cls}{background:${lightBg};}` +
    (darkBg !== lightBg ? `:root[data-theme="dark"] .${cls}{background:${darkBg};}` : '');

  const style: CSSProperties = {
    position: 'absolute',
    inset: 0,
    // Behind the section's content (in-flow, non-positioned) but still
    // in front of the section's own background — works because every
    // host region is an `isolation: isolate` stacking context, so this
    // negative layer can't slip below the section background.
    zIndex: -1,
    pointerEvents: 'none',
    opacity: o,
    ...(b > 0 ? { filter: `blur(${b}px)` } : null),
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <BlockRoot as="div" aria-hidden className={cls} style={style} />
    </>
  );
}
